// utility/analytics/events.ts
export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type ProductSummary = {
  product_id: string;
  name: string;
  category?: string;
  price?: number;
  quantity?: number;
  currency?: string;
};

export type UtmKey = (typeof UTM_KEYS)[number];

export type UtmParams = Partial<Record<UtmKey, string>>;

export type PageMetadata = {
  page_name?: string;
  page_url?: string;
  referrer_page?: string;
  scroll_depth_percent?: number;
} & UtmParams;

type WithPageMetadata<T extends Record<string, any> = Record<string, never>> =
  T & PageMetadata;

type AnalyticsEventDefinition<Name extends string, Payload extends Record<string, any>> = {
  name: Name;
  payload: Payload;
};

const defineAnalyticsEvents = <
  const Definitions extends Record<
    string,
    AnalyticsEventDefinition<string, Record<string, any>>
  >,
>(
  definitions: Definitions,
) => definitions;

const analyticsEventDefinitions = defineAnalyticsEvents({
  PAGE_VIEWED: {
    name: 'page_viewed',
    payload: {} as WithPageMetadata<{
      page_name: string;
      page_url: string;
      referrer_page?: string;
      web_vitals?: Record<string, number>;
    }>,
  },
  RATING_PROMPT_VIEWED: {
    name: 'rating_prompt_viewed',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  RATING_SELECTED: {
    name: 'rating_selected',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      rating_value: 1 | 2 | 3 | 4 | 5;
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  RATING_SUBMIT_CLICKED: {
    name: 'rating_submit_clicked',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      rating_value: 1 | 2 | 3 | 4 | 5;
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  RATING_SUBMITTED: {
    name: 'rating_submitted',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      rating_value: 1 | 2 | 3 | 4 | 5;
      submission_time_ms?: number;
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  RATING_SUBMISSION_FAILED: {
    name: 'rating_submission_failed',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      rating_value: 1 | 2 | 3 | 4 | 5;
      error_type: 'network' | 'validation' | 'server';
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  RATING_ABANDONED: {
    name: 'rating_abandoned',
    payload: {} as WithPageMetadata<{
      rating_type: 'platform' | 'seller' | 'sku';
      entity_id: string;
      last_step_completed: 'rating_prompt_viewed' | 'rating_selected' | 'rating_submit_clicked';
      rating_value?: 1 | 2 | 3 | 4 | 5;
      entry_point?: string;
      platform: 'web' | 'ios' | 'android';
    }>,
  },
  NAV_BAR_CLICK: {
    name: 'nav_bar_click',
    payload: {} as WithPageMetadata<{
      cta_text: string;
      placement?: string;
    }>,
  },
  LOCATION_CTA_CLICKED: {
    name: 'location_cta_clicked',
    payload: {} as WithPageMetadata<{
      placement?: string;
    }>,
  },
  TAB_CLICKED: {
    name: 'tab_clicked',
    payload: {} as WithPageMetadata<{
      cta_name: string;
      category_name?: string;
      subcategory_name?: string;
    }>,
  },
  BANNER_VIEWED: {
    name: 'banner_viewed',
    payload: {} as WithPageMetadata<{
      banner_id: string;
      banner_position?: string;
      campaign_name?: string;
      link_url?: string;
      product_id?: string;
      product_name?: string;
    }>,
  },
  BANNER_CLICKED: {
    name: 'banner_clicked',
    payload: {} as WithPageMetadata<{
      banner_id: string;
      banner_position?: string;
      campaign_name?: string;
      link_url?: string;
      product_id?: string;
      product_name?: string;
    }>,
  },
  SEARCH_PERFORMED: {
    name: 'search_performed',
    payload: {} as WithPageMetadata<{
      search_term: string;
      num_results?: number;
      result_status?: 'no_results' | 'results_found' | 'empty_query';
      scope?: string;
      provider_id?: string;
      filters?: Record<string, unknown>;
    }>,
  },
  SEARCH_INITIATED: {
    name: 'search_initiated',
    payload: {} as WithPageMetadata<{
      search_term?: string;
    }>,
  },
  SEARCH_SUGGESTION_CLICKED: {
    name: 'search_suggestion_clicked',
    payload: {} as WithPageMetadata<{
      search_term: string;
      clicked_suggestion: string;
    }>,
  },
  SEARCH_RESULT_CLICKED: {
    name: 'search_result_clicked',
    payload: {} as WithPageMetadata<{
      search_term: string;
      clicked_result?: string;
      result_position?: number;
    }>,
  },
  CTA_EVENT: {
    name: 'cta_event',
    payload: {} as WithPageMetadata<{
      action?: string;
      cta_name?: string;
      [key: string]: any;
    }>,
  },
  COMPOUND_VIEWED: {
    name: 'compound_viewed',
    payload: {} as WithPageMetadata<{
      page_name?: string;
    }>,
  },
  FILTER_APPLIED: {
    name: 'filter_applied',
    payload: {} as WithPageMetadata<{
      filter_type: string;
      filter_value: string;
    }>,
  },
  SORT_APPLIED: {
    name: 'sort_applied',
    payload: {} as WithPageMetadata<{
      sort_type: string;
      list_name?: string;
    }>,
  },
  COUPON_APPLIED: {
    name: 'coupon_applied',
    payload: {} as WithPageMetadata<{
      coupon_code: string;
      cart_total_value?: number;
      discount_amount?: number;
    }>,
  },
  MY_ORDERS_CARD_CLICKED: {
    name: 'my_orders_card_clicked',
    payload: {} as WithPageMetadata<{
      category_name?: string;
      subcategory?: string;
      store_name?: string;
      provider_id?: string;
      product_id?: string;
      product_name?: string;
      product_price?: number;
      delivery_status?: string;
    }>,
  },
  PLP_VIEWED: {
    name: 'plp_viewed',
    payload: {} as WithPageMetadata<{
      category_name: string;
      num_products_displayed?: number;
      page_number?: number;
      filters_applied?: string;
      layout_type?: string;
      list_name?: string;
      scroll_depth_percent?: number;
    }>,
  },
  PLP_PRODUCT_CLICKED: {
    name: 'plp_product_clicked',
    payload: {} as WithPageMetadata<{
      product_id: string;
      product_name: string;
      category: string;
      price?: number;
      products?: ProductSummary[];
      currency?: string;
      position_index?: number;
      filters_active?: string;
      discount_applied?: boolean;
      seller_name?: string;
      in_stock?: boolean;
      list_name?: string;
    }>,
  },
  GIFTCARD_PRODUCT_CLICKED: {
    name: 'giftcard_product_clicked',
    payload: {} as WithPageMetadata<{
      product_id: string;
      product_name: string;
      category: string;
      price?: number;
      products?: ProductSummary[];
      currency?: string;
      position_index?: number;
      filters_active?: string;
      discount_applied?: boolean;
      seller_name?: string;
      in_stock?: boolean;
      list_name?: string;
    }>,
  },
  PRODUCT_VIEWED: {
    name: 'product_viewed',
    payload: {} as WithPageMetadata<{
      product_id: string;
      product_name: string;
      category: string;
      price?: number;
      currency?: string;
      discount_applied?: boolean;
      seller_name?: string;
      provider_id?: string;
      action_source?: string;
    }>,
  },
  DENOMINATION_SELECTED: {
    name: 'denomination_selected',
    payload: {} as WithPageMetadata<{
      giftcard_id: string;
      giftcard_name: string;
      denomination: number;
      quantity: number;
      action: 'increase' | 'decrease';
    }>,
  },
  ADD_TO_CART: {
    name: 'add_to_cart',
    payload: {} as WithPageMetadata<{
      product_id: string;
      product_name: string;
      category: string;
      price?: number;
      currency?: string;
      discount_applied?: boolean;
      seller_name?: string;
      cart_value?: number;
      quantity?: number;
      provider_id?: string;
      action_source?: string;
      products?: ProductSummary[];
    }>,
  },
  REMOVE_FROM_CART: {
    name: 'remove_from_cart',
    payload: {} as WithPageMetadata<{
      product_id: string;
      product_name: string;
      category: string;
      price?: number;
      currency?: string;
      discount_applied?: boolean;
      seller_name?: string;
      cart_value?: number;
      quantity?: number;
      products?: ProductSummary[];
    }>,
  },
  VIEW_CART: {
    name: 'view_cart',
    payload: {} as WithPageMetadata<{
      cart_id: string;
      products: ProductSummary[];
      cart_total_value: number;
      num_unique_items: number;
      currency?: string;
      discount_total?: number;
      source_page?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  CHECKOUT_STARTED: {
    name: 'checkout_started',
    payload: {} as WithPageMetadata<{
      products: ProductSummary[];
      checkout_step?: number;
      cart_total_value?: number;
      currency?: string;
      shipping_method?: string;
      payment_method_selected?: string;
      discount_total?: number;
      offers_applied?: string;
      transaction_id?: string;
      items?: number;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  PAYMENT_INITIATED: {
    name: 'payment_initiated/order_initiated',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      delivery_type?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  PAYMENT_FAILED: {
    name: 'payment_failed',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      failure_reason?: string;
      delivery_type?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
    ORDER_FAILED: {
    name: 'order_failed',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      failure_reason?: string;
      delivery_type?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  PAYMENT_SUCCESS: {
    name: 'payment_success',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      delivery_type?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  ORDER_CONFIRMED: {
    name: 'order_confirmed',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      delivery_type?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  ORDER_CANCELLED: {
    name: 'order_cancelled',
    payload: {} as WithPageMetadata<{
      order_id: string;
      products: ProductSummary[];
      order_value: number;
      discount_total?: number;
      shipping_cost?: number;
      currency?: string;
      payment_method?: string;
      payment_status?: string;
      delivery_type?: string;
 
      cancel_reason?: string;
      coupon_applied?: boolean;
      coupon_code?: string;
    }>,
  },
  NETWORK_CALL: {
    name: 'network_call',
    payload: {} as WithPageMetadata<{
      url?: string;
      method?: string;
      status?: number;
      duration_ms?: number;
      transfer_size?: number;
      error_message?: string;
      total_calls?: number;
      requests?: Array<{
        url?: string;
        method?: string;
        status?: number;
        duration_ms?: number;
        transfer_size?: number;
        error_message?: string;
      }>;
    }>,
  },
  PREDICTOMETER_CLICKED: {
  name: 'Predictometer_clicked',
  payload: {} as WithPageMetadata<{
    team1: string;
    team2: string;
    team_selected: string;
    match_no: string | number;
  }>,
},
});

export const AnalyticsEvents = Object.freeze(
  Object.fromEntries(
    Object.entries(analyticsEventDefinitions).map(([key, value]) => [
      key,
      value.name,
    ]),
  ) as {
    readonly [Key in keyof typeof analyticsEventDefinitions]: (typeof analyticsEventDefinitions)[Key]['name'];
  },
);

type AnalyticsEventPayloadMap = {
  [Key in keyof typeof analyticsEventDefinitions as (typeof analyticsEventDefinitions)[Key]['name']]:
    (typeof analyticsEventDefinitions)[Key]['payload'];
};

export type AnalyticsEventName =
  | (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]
  | (string & {});

export type AnalyticsEventPayload<EventName extends AnalyticsEventName> =
  EventName extends keyof AnalyticsEventPayloadMap
    ? AnalyticsEventPayloadMap[EventName]
    : Record<string, any>;

export interface AnalyticsUserProfile {
  id: string;
  name?: string;
  email?: string;
  phone?: string;
  signup_date?: string;
  user_type?: string;
  country?: string;
  city?: string;
  total_orders?: number;
  last_order_date?: string;
}

export interface AnalyticsService {
  track<EventName extends AnalyticsEventName>(
    event: EventName,
    payload?: AnalyticsEventPayload<EventName>,
  ): void;
  setUserProfile(profile: AnalyticsUserProfile): void;
  isEnabled(): boolean;
  isReady(): boolean;
  reset(): void;
  flush(): Promise<void>;
}
